import type { StateCreator } from 'zustand'
import {
  getWorkflows,
  approveWorkflow,
  rejectWorkflow,
  executeWorkflow,
  executeWorkflowWithExecutor,
  retryWorkflow,
  triggerAgentScan,
  triggerProfileScan,
  type AgentScanRequest,
  type ProfileScanRequest,
  type ProfileScanResult,
} from '@/shared/lib/api'
import { getAuthHeader } from '@/shared/lib/auth'
import { logger } from '@/shared/lib/logger'
import type { Workflow as APIWorkflow } from '@/shared/types'
import type { OrchWorkflow } from '../../types'
import {
  apiWorkflowToOrch,
  optimisticApprove,
  optimisticReject,
  optimisticExecute,
  optimisticRetry,
} from '../../lib/workflowTransformers'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001'

export interface PipelineGatherRequest {
  repository_path: string
  strategy_type: string
  strategy_config: Record<string, unknown>
}

export interface PipelineAnalyzeRequest {
  analyzer_type: string
  prompt_id?: string
  agent_definition_id?: number
  focus_area?: string
}

export interface PipelineScanContext {
  id: number
  strategy_type: string
  strategy_config: Record<string, unknown>
  repository_path: string
  status: 'gathering' | 'ready' | 'failed'
  files: string[]
  raw_content: string
  content_size: number
  file_count: number
  metadata: Record<string, unknown>
  error?: string
  created_at: string
  completed_at?: string
}

export interface PipelineScanAnalysis {
  id: number
  context_id: number
  analyzer_type: string
  prompt_id?: string
  status: 'pending' | 'analyzing' | 'ready' | 'failed'
  findings: PipelineFinding[]
  raw_llm_output?: string
  stats: Record<string, unknown>
  error?: string
  created_at: string
  completed_at?: string
}

export interface PipelineFinding {
  title: string
  description: string
  type: string
  priority: string
  target_files: string[]
  suggested_changes?: Record<string, unknown>
  implementation_plan?: Record<string, unknown>
}

export interface WorkflowsSlice {
  workflows: OrchWorkflow[]
  workflowsLoading: boolean
  workflowsError: string | null
  /** Set by TelemetryPane (or other UI) to request opening a workflow detail drawer */
  viewWorkflowId: number | null
  setViewWorkflowId: (id: number | null) => void
  fetchWorkflows: (status?: string) => Promise<void>
  refreshWorkflows: () => Promise<void>
  approveWorkflow: (id: number, notes?: string) => Promise<void>
  rejectWorkflow: (id: number, notes?: string) => Promise<void>
  executeWorkflow: (id: number, executor?: string, dryRun?: boolean) => Promise<void>
  retryWorkflow: (id: number) => Promise<void>
  triggerAgentScan: (request: AgentScanRequest) => Promise<APIWorkflow>
  triggerProfileScan: (request: ProfileScanRequest) => Promise<ProfileScanResult>
  pipelineGatherContext: (request: PipelineGatherRequest) => Promise<PipelineScanContext>
  pipelineAnalyzeContext: (contextId: number, request: PipelineAnalyzeRequest) => Promise<PipelineScanAnalysis>
  pipelineCreateWorkflows: (analysisId: number, findingIndices: number[]) => Promise<number[]>
}

export const createWorkflowsSlice: StateCreator<
  WorkflowsSlice,
  [],
  [],
  WorkflowsSlice
> = (set, get) => ({
  workflows: [],
  workflowsLoading: false,
  workflowsError: null,
  viewWorkflowId: null,
  setViewWorkflowId: (id) => set({ viewWorkflowId: id }),
  fetchWorkflows: async (status?: string) => {
    set({ workflowsLoading: true, workflowsError: null })
    try {
      const workflows = await getWorkflows(status)
      set({ workflows: workflows.map(apiWorkflowToOrch), workflowsLoading: false })
    } catch (error) {
      set({
        workflowsError: error instanceof Error ? error.message : 'Failed to load workflows',
        workflowsLoading: false,
      })
    }
  },
  refreshWorkflows: async () => {
    try {
      const workflows = await getWorkflows()
      set({ workflows: workflows.map(apiWorkflowToOrch) })
    } catch (error) {
      logger.error('Failed to refresh workflows', { error })
    }
  },
  approveWorkflow: async (id: number, notes?: string) => {
    const prevWorkflows = get().workflows
    try {
      // Optimistic update
      set((state) => ({
        workflows: state.workflows.map((w) => (w.id === id ? optimisticApprove(w) : w)),
      }))
      await approveWorkflow(id, notes)
      await get().refreshWorkflows()
    } catch (error) {
      // Rollback on error
      set({ workflows: prevWorkflows })
      await get().refreshWorkflows()
      logger.error('Failed to approve workflow', { error })
      throw error
    }
  },
  rejectWorkflow: async (id: number, notes?: string) => {
    const prevWorkflows = get().workflows
    try {
      // Optimistic update
      set((state) => ({
        workflows: state.workflows.map((w) => (w.id === id ? optimisticReject(w) : w)),
      }))
      await rejectWorkflow(id, notes)
      await get().refreshWorkflows()
    } catch (error) {
      // Rollback on error
      set({ workflows: prevWorkflows })
      await get().refreshWorkflows()
      logger.error('Failed to reject workflow', { error })
      throw error
    }
  },
  executeWorkflow: async (id: number, executor?: string, dryRun = false) => {
    const prevWorkflows = get().workflows
    try {
      // Optimistic update
      set((state) => ({
        workflows: state.workflows.map((w) => (w.id === id ? optimisticExecute(w) : w)),
      }))
      if (executor) {
        await executeWorkflowWithExecutor(id, executor as string, dryRun)
      } else {
        await executeWorkflow(id, dryRun)
      }
      await get().refreshWorkflows()
    } catch (error) {
      // Rollback on error
      set({ workflows: prevWorkflows })
      await get().refreshWorkflows()
      logger.error('Failed to execute workflow', { error })
      throw error
    }
  },
  retryWorkflow: async (id: number) => {
    const prevWorkflows = get().workflows
    try {
      // Optimistic update
      set((state) => ({
        workflows: state.workflows.map((w) => (w.id === id ? optimisticRetry(w) : w)),
      }))
      await retryWorkflow(id)
      await get().refreshWorkflows()
    } catch (error) {
      // Rollback on error
      set({ workflows: prevWorkflows })
      await get().refreshWorkflows()
      logger.error('Failed to retry workflow', { error })
      throw error
    }
  },
  triggerAgentScan: async (request: AgentScanRequest) => {
    try {
      const workflow = await triggerAgentScan(request)
      await get().refreshWorkflows()
      return workflow
    } catch (error) {
      logger.error('Failed to trigger agent scan', { error })
      throw error
    }
  },
  triggerProfileScan: async (request: ProfileScanRequest) => {
    try {
      const result = await triggerProfileScan(request)
      if (Array.isArray(result)) {
        await get().refreshWorkflows()
      }
      return result
    } catch (error) {
      logger.error('Failed to trigger profile scan', { error })
      throw error
    }
  },
  pipelineGatherContext: async (request) => {
    const response = await fetch(`${API_URL}/pipeline/context`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.detail || 'Failed to gather context')
    }
    return response.json()
  },
  pipelineAnalyzeContext: async (contextId, request) => {
    const response = await fetch(`${API_URL}/pipeline/context/${contextId}/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(request),
    })
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.detail || 'Analysis failed')
    }
    return response.json()
  },
  pipelineCreateWorkflows: async (analysisId, findingIndices) => {
    const response = await fetch(`${API_URL}/pipeline/analysis/${analysisId}/workflows`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify({ finding_indices: findingIndices }),
    })
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.detail || 'Failed to create workflows')
    }
    const workflows = await response.json()
    get().refreshWorkflows()
    return workflows.map((w: { id: number }) => w.id)
  },
})

